import { Advice } from 'types/Checklist';
import GenericQuestion, { QuestionType, YesNoChoice } from 'types/Question';

import { KioskPreferencesQuestionsIds } from './constants';

import {
  DocumentStatus, MobilityDocument, SelfService, SelfServiceOrigin, SelfServiceType,
} from './types/SelfService';
import { SelfServiceStepConfig } from './types/SelfServiceStepConfig';

const PXL_CHECK_ELIGIBLE_STATUSES = [DocumentStatus.REQUESTED, DocumentStatus.SKIPPED];

export const isPxlCheckEligible = (status: DocumentStatus): boolean => PXL_CHECK_ELIGIBLE_STATUSES.includes(status);

export type GetAdvicesFunc = (selfService: SelfService) => Array<Advice>;

export const getExtras: GetAdvicesFunc = (selfService) => selfService.checklists?.flatMap(
  ({ items }) => items?.flatMap(({ advices }) => advices ?? []) ?? [],
) ?? [];

export const getProactiveWorks: GetAdvicesFunc = (selfService) => selfService?.proactiveChecklists?.flatMap(({ items }) => items?.flatMap(
  ({ advices = [] }) => advices.map((advice) => ({ ...advice, mandatory: true })) ?? [],
)) ?? [];

/**
 * @description This is more a placeholder than a function intended to be used.
 * @see `src/components/Extras/Extras.tsx`
 */
export const getAdvices: GetAdvicesFunc = (selfService) => [
  ...getExtras(selfService),
  ...getProactiveWorks(selfService),
];

export const generateKioskPreferencesQuestions = (selfService: SelfService) => {
  const getChoiceByValue = (value: boolean): YesNoChoice => {
    if (value !== undefined) {
      return (value ? YesNoChoice.YES : YesNoChoice.NO);
    }
    return undefined;
  };

  const questions: GenericQuestion[] = [
    {
      id: KioskPreferencesQuestionsIds.CHECK_OUT,
      mandatory: true,
      title: {
        id: 'page.kioskUsage.question.checkOut',
        defaultMessage: 'I wish to use the kiosk to retrieve my keys after my appointment.',
      },
      type: QuestionType.YES_NO,
      comment: selfService.kioskPreference?.checkOutComment,
      response: getChoiceByValue(selfService.kioskPreference?.checkOut),
    },
  ];

  if (selfService.origin === SelfServiceOrigin.HOME) {
    questions.unshift({
      id: KioskPreferencesQuestionsIds.CHECK_IN,
      mandatory: true,
      title: {
        id: 'page.kioskUsage.question.checkIn',
        defaultMessage: 'I wish to use the kiosk to drop my keys off before my appointment.',
      },
      type: QuestionType.YES_NO,
      comment: selfService.kioskPreference?.checkInComment,
      response: getChoiceByValue(selfService.kioskPreference?.checkIn),
    });
  }

  return questions;
};

export const isKioskPreferenceQuestionVisible = (
  id: KioskPreferencesQuestionsIds,
  type: SelfServiceType,
  origin: SelfServiceOrigin,
  config: SelfServiceStepConfig,
): boolean => {
  const isCheckInQuestion = id === KioskPreferencesQuestionsIds.CHECK_IN;
  const isCheckOutQuestion = id === KioskPreferencesQuestionsIds.CHECK_OUT;

  const hasKioskCheckIn = config.selfServiceAvailableTypes?.CHECK_IN?.includes(SelfServiceOrigin.KIOSK);
  const hasKioskCheckOut = config.selfServiceAvailableTypes?.CHECK_OUT?.includes(SelfServiceOrigin.KIOSK);

  if (isCheckInQuestion) {
    return (
      type === SelfServiceType.CHECK_IN
      && origin === SelfServiceOrigin.HOME
      && hasKioskCheckIn
    );
  }

  return isCheckOutQuestion ? hasKioskCheckOut : false;
};

const DEFAULT_PROPERTIES_TO_REMOVE: [keyof Pick<MobilityDocument, 'medias'>] = ['medias'];

/**
 * @description Removes properties (extracted from `PropertiesToRemove` type) from `values`.
 * @argument values The object to remove the properties from.
 * @argument propertiesToRemove (Optional) An array containing the list of properties names to remove.
 */
export function filterObjectProperties<
  Values extends { [key in PropertiesToRemove]?: unknown }, // The object to remove the properties from
  PropertiesToRemove extends string = typeof DEFAULT_PROPERTIES_TO_REMOVE[number], // Optional type to indicate properties to remove
  ReturnType = Omit<Values, PropertiesToRemove>, // The object without removed properties
>(values: Values, propertiesToRemove: PropertiesToRemove[] = DEFAULT_PROPERTIES_TO_REMOVE as unknown as PropertiesToRemove[]): ReturnType {
  if (typeof values === 'object') {
    if (Array.isArray(values)) {
      return values.map((value) => filterObjectProperties(value, propertiesToRemove)) as ReturnType;
    }

    return Object.entries(values).reduce((acc, [key, value]) => ({
      ...acc,
      ...(propertiesToRemove.includes(key as PropertiesToRemove) ? undefined : {
        [key]: filterObjectProperties(value as Values, propertiesToRemove),
      }),
    }), {} as ReturnType);
  }

  return values;
}
